/**
 * AddTailsButton — library-card control to append new tail-end clips to
 * an ALREADY-PUBLISHED story. The story keeps its slug, URL, views and
 * podcast history; the video is re-rendered in place with the new clips
 * crossfaded on the end (before the Storylo end-card).
 */
import { useRef, useState } from "react";
import axios from "axios";
import { Clapperboard, Loader2, X, Link2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddTailsButton({ video, uploadFileInChunks, onQueued }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [fetched, setFetched] = useState([]); // {upload_id, filename, label}
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [resetFirst, setResetFirst] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const inputRef = useRef(null);

  const removeAllTails = async () => {
    setBusy(true);
    setProgress("Removing added clips…");
    try {
      await axios.post(`${API}/videos/${video.id}/tails/reset`, {}, { timeout: 180_000 });
      toast.success(`Added clips removed — "${video.title}" is re-rendering from the original`);
      setOpen(false);
      onQueued?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove the clips");
    } finally {
      setBusy(false);
      setProgress("");
      setConfirmRemove(false);
    }
  };

  const totalClips = files.length + fetched.length;

  const fetchFromUrl = async () => {
    const u = url.trim();
    if (!u) return;
    setFetching(true);
    try {
      const fd = new FormData();
      fd.append("url", u);
      const { data } = await axios.post(`${API}/fetch-url`, fd, { timeout: 10 * 60 * 1000 });
      setFetched((prev) => [...prev, { upload_id: data.upload_id, filename: data.filename, label: u }]);
      setUrl("");
      toast.success("Clip fetched from URL");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch that URL");
    } finally {
      setFetching(false);
    }
  };

  const submit = async () => {
    if (totalClips === 0) return;
    setBusy(true);
    try {
      const uploads = fetched.map((f) => ({ upload_id: f.upload_id, filename: f.filename }));
      for (let i = 0; i < files.length; i++) {
        setProgress(`Uploading clip ${i + 1} of ${files.length}…`);
        const up = await uploadFileInChunks(files[i], () => {});
        uploads.push({ upload_id: up.upload_id, filename: up.filename });
      }
      setProgress("Starting re-render…");
      const fd = new FormData();
      fd.append("tail_uploads", JSON.stringify(uploads));
      if (resetFirst) fd.append("reset_tails", "true");
      const { data } = await axios.post(`${API}/videos/${video.id}/add-tails`, fd, { timeout: 180_000 });
      toast.success(`${data.added} clip(s) added — "${video.title}" is re-rendering`);
      setOpen(false);
      setFiles([]);
      setFetched([]);
      onQueued?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Couldn't add clips");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center justify-center gap-1.5"
        data-testid={`library-add-tails-${video.id}`}
        title="Append new tail-end clips to this published story and re-render it — URL, views and podcast history are kept"
      >
        <Clapperboard className="w-3.5 h-3.5" />
        Add clips
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="add-tails-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }}
        >
          <div className="panel max-w-lg w-full max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-eyebrow mb-1">Add tail clips</div>
                <div className="text-sm text-[color:var(--muted)] truncate max-w-[22rem]">{video.title}</div>
              </div>
              <button
                onClick={() => !busy && setOpen(false)}
                className="text-[color:var(--muted)] hover:text-white"
                data-testid="add-tails-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-xs text-[color:var(--muted)] mb-4 leading-relaxed">
              New clips are crossfaded onto the <strong className="text-white">end</strong> of the
              existing video (before the Storylo end-card). The story keeps its link, views and
              podcast history — the video simply re-renders with the extra footage.
            </div>

            <input
              ref={inputRef}
              type="file"
              multiple
              accept="video/*,image/*,.mp4,.mov,.mkv,.webm,.avi,.jpg,.jpeg,.png,.webp"
              className="hidden"
              data-testid="add-tails-file-input"
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                if (picked.length) setFiles((prev) => [...prev, ...picked]);
                e.target.value = "";
              }}
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="w-full py-3 rounded-lg border border-dashed border-white/20 hover:border-[color:var(--accent)] text-sm text-[color:var(--muted)] hover:text-[color:var(--accent)] transition-colors disabled:opacity-40"
              data-testid="add-tails-pick-files-btn"
            >
              Choose video files or photos…
            </button>

            <div className="flex items-center gap-2 mt-3">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") fetchFromUrl(); }}
                placeholder="…or paste a clip URL (TikTok, Instagram, X, direct MP4)"
                className="flex-1 bg-white/[.05] border border-white/10 rounded-lg px-3 py-2 text-sm placeholder:text-[color:var(--muted)] focus:outline-none focus:border-[color:var(--accent)]"
                data-testid="add-tails-url-input"
                disabled={busy || fetching}
              />
              <button
                onClick={fetchFromUrl}
                disabled={busy || fetching || !url.trim()}
                className="px-3 py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors disabled:opacity-40"
                data-testid="add-tails-fetch-btn"
                title="Download the clip from this URL"
              >
                {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-[11px] text-amber-300/80 mt-1.5">
              YouTube &amp; Facebook block downloads from cloud servers — save those clips to your
              device first, then use "Choose video files" above.
            </div>

            {(video.tail_count || 0) > 0 && (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-400/25 bg-red-500/[.06] px-3 py-2.5">
                <div className="text-xs text-[color:var(--muted)]">
                  <strong className="text-red-300">Undo:</strong> remove all{" "}
                  {video.tail_count} added clip{video.tail_count > 1 ? "s" : ""} and re-render the
                  original video. Use this if an added clip is corrupted.
                </div>
                <button
                  onClick={() => {
                    if (busy) return;
                    if (!confirmRemove) {
                      setConfirmRemove(true);
                      setTimeout(() => setConfirmRemove(false), 5000);
                      return;
                    }
                    removeAllTails();
                  }}
                  disabled={busy}
                  className={`shrink-0 text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-md border transition-colors disabled:opacity-40 ${confirmRemove ? "border-red-400 text-red-300 bg-red-500/10" : "border-white/15 hover:border-red-400 hover:text-red-300"}`}
                  data-testid="remove-tails-btn"
                >
                  {confirmRemove ? "Really remove?" : "Remove clips"}
                </button>
              </div>
            )}

            {(video.tail_count || 0) > 0 && (
              <label className="flex items-start gap-2 mt-3 text-xs text-[color:var(--muted)] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={resetFirst}
                  onChange={(e) => setResetFirst(e.target.checked)}
                  disabled={busy}
                  className="mt-0.5 accent-[color:var(--accent)]"
                  data-testid="add-tails-reset-checkbox"
                />
                <span>
                  <strong className="text-amber-300">Start fresh:</strong> remove the{" "}
                  {video.tail_count} previously added clip{video.tail_count > 1 ? "s" : ""} first,
                  so only the clips chosen here are appended. Use this if an earlier attempt was
                  interrupted or added a duplicate.
                </span>
              </label>
            )}

            {(files.length > 0 || fetched.length > 0) && (
              <div className="mt-4 space-y-1.5" data-testid="add-tails-clip-list">
                {fetched.map((f, i) => (
                  <div key={`u${i}`} className="flex items-center gap-2 text-xs bg-white/[.04] rounded px-2 py-1.5">
                    <Link2 className="w-3.5 h-3.5 text-[color:var(--muted)] shrink-0" />
                    <span className="flex-1 truncate">{f.label}</span>
                    <button
                      onClick={() => setFetched((prev) => prev.filter((_, j) => j !== i))}
                      disabled={busy}
                      className="text-[color:var(--muted)] hover:text-red-400"
                      data-testid={`add-tails-remove-url-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {files.map((f, i) => (
                  <div key={`f${i}`} className="flex items-center gap-2 text-xs bg-white/[.04] rounded px-2 py-1.5">
                    <Clapperboard className="w-3.5 h-3.5 text-[color:var(--muted)] shrink-0" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-[color:var(--muted)]">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                    <button
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      disabled={busy}
                      className="text-[color:var(--muted)] hover:text-red-400"
                      data-testid={`add-tails-remove-file-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-white/10">
              <div className="text-xs text-[color:var(--muted)]" data-testid="add-tails-progress">
                {busy ? progress : totalClips ? `${totalClips} clip(s) ready` : ""}
              </div>
              <button
                onClick={submit}
                disabled={busy || totalClips === 0}
                className="px-4 py-2 rounded bg-[color:var(--accent)] text-black text-sm font-medium hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="add-tails-submit-btn"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Add &amp; re-render
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
